import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const SHAPE_COLORS = [
  '#ffffff', '#f3f4f6', '#e5e7eb', '#d1d5db', '#9ca3af', '#6b7280', '#4b5563', '#374151', '#1f2937', '#111827',
  '#fef2f2', '#fee2e2', '#fecaca', '#fca5a5', '#f87171', '#ef4444', '#dc2626', '#b91c1c', '#991b1b', '#7f1d1d',
  '#fef3c7', '#fde68a', '#fcd34d', '#fbbf24', '#f59e0b', '#d97706', '#b45309', '#92400e', '#78350f', '#451a03',
  '#fefce8', '#fef9c3', '#fef08a', '#fde047', '#facc15', '#eab308', '#ca8a04', '#a16207', '#854d0e', '#713f12',
  '#f0fdf4', '#dcfce7', '#bbf7d0', '#86efac', '#4ade80', '#22c55e', '#16a34a', '#15803d', '#166534', '#14532d',
  '#f0f9ff', '#e0f2fe', '#bae6fd', '#7dd3fc', '#38bdf8', '#0ea5e9', '#0284c7', '#0369a1', '#075985', '#0c4a6e',
  '#fef7ff', '#f3e8ff', '#e9d5ff', '#d8b4fe', '#c084fc', '#a855f7', '#9333ea', '#7c3aed', '#6b21a8', '#581c87',
  '#fdf2f8', '#fce7f3', '#fbcfe8', '#f9a8d4', '#f472b6', '#ec4899', '#db2777', '#be185d', '#9d174d', '#831843'
];

const STROKE_COLORS = [
  '#000000', '#374151', '#6b7280', '#9ca3af', '#d1d5db', '#e5e7eb', '#f3f4f6', '#ffffff',
  '#dc2626', '#ea580c', '#d97706', '#ca8a04', '#65a30d', '#16a34a', '#0d9488', '#0891b2',
  '#2563eb', '#7c3aed', '#db2777', '#be185d', '#9d174d', '#831843', '#581c87', '#1e40af'
];

const STROKE_STYLES = [
  { value: 'solid', label: 'Solid', icon: '━' },
  { value: 'dashed', label: 'Dashed', icon: '┅' },
  { value: 'dotted', label: 'Dotted', icon: '┄' }
];

const LINE_END_STYLES = [
  { value: 'none', label: 'None', icon: '—' },
  { value: 'arrow', label: 'Arrow', icon: '→' },
  { value: 'circle', label: 'Circle', icon: '●' },
  { value: 'square', label: 'Square', icon: '■' }
];

// Иконки для кнопок
const IconFill = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);

const IconStroke = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10"/>
  </svg>
);

const IconStyle = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
    <path d="M12 2v20"/>
  </svg>
);

const IconThickness = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18M3 12h18M3 18h18"/>
  </svg>
);

const IconDuplicate = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);

const IconDelete = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
  </svg>
);

const IconBringForward = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/>
  </svg>
);

const IconSendBackward = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 3H5a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2z"/>
    <path d="M8 12h8"/>
  </svg>
);

const ShapeToolbar = ({ shape, onShapeChange, onDuplicate, onDelete, onBringForward, onSendBackward, x, y, onClose }) => {
  const [activeTab, setActiveTab] = useState(() => {
    // Для линий и стрелок начинаем с таба stroke, для остальных с fill
    return shape?.type === 'line' || shape?.type === 'arrow' ? 'stroke' : 'fill';
  });
  const [currentShape, setCurrentShape] = useState(shape);

  // Обновляем локальное состояние при изменении пропа shape
  useEffect(() => {
    setCurrentShape(shape);
    // Обновляем активный таб при изменении типа фигуры
    if (shape?.type === 'line' || shape?.type === 'arrow') {
      setActiveTab('stroke');
    } else {
      setActiveTab('fill');
    }
  }, [shape]);



  if (!shape) return null;

  const handleFillChange = (fill) => {
    // При изменении заливки сохраняем обводку
    const updatedShape = { ...currentShape, fill };
    // Если у фигуры нет цвета обводки, устанавливаем черный по умолчанию
    if (!updatedShape.color) {
      updatedShape.color = '#000';
    }
    setCurrentShape(updatedShape);
    onShapeChange(updatedShape);
  };

  const handleStrokeChange = (color) => {
    // При изменении обводки сохраняем заливку
    const updatedShape = { ...currentShape, color };
    setCurrentShape(updatedShape);
    onShapeChange(updatedShape);
  };

  const handleStrokeWidthChange = (strokeWidth) => {
    // При изменении толщины обводки сохраняем все остальные свойства
    const updatedShape = { ...currentShape, strokeWidth };
    // Если у фигуры нет цвета обводки, устанавливаем черный по умолчанию
    if (!updatedShape.color) {
      updatedShape.color = '#000';
    }
    setCurrentShape(updatedShape);
    onShapeChange(updatedShape);
  };

  const handleStrokeStyleChange = (strokeStyle) => {
    const updatedShape = { ...currentShape, strokeStyle };
    setCurrentShape(updatedShape);
    onShapeChange(updatedShape);
  };

  const handleOpacityChange = (opacity) => {
    const updatedShape = { ...currentShape, opacity };
    setCurrentShape(updatedShape);
    onShapeChange(updatedShape);
  };

  const handleBorderRadiusChange = (borderRadius) => {
    const updatedShape = { ...currentShape, borderRadius };
    setCurrentShape(updatedShape);
    onShapeChange(updatedShape);
  };

  const handleLineEndStyleChange = (endStyle, end) => {
    const updatedShape = { 
      ...currentShape, 
      [end === 'start' ? 'startEndStyle' : 'endEndStyle']: endStyle 
    };
    setCurrentShape(updatedShape);
    onShapeChange(updatedShape);
  };

  const handleDuplicate = () => {
    onDuplicate(shape);
    onClose();
  };

  const handleDelete = () => {
    onDelete(shape.id);
    onClose();
  };

  const handleBringForward = () => {
    console.log('Bring Forward clicked for shape:', currentShape.id);
    onBringForward(currentShape.id);
  };

  const handleSendBackward = () => {
    console.log('Send Backward clicked for shape:', currentShape.id);
    onSendBackward(currentShape.id);
  };

  return createPortal(
    <>
      <style>
        {`
          input[type="range"]::-webkit-slider-thumb {
            -webkit-appearance: none;
            appearance: none;
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #3b82f6;
            cursor: pointer;
            border: 2px solid #fff;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }
          input[type="range"]::-moz-range-thumb {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: #3b82f6;
            cursor: pointer;
            border: 2px solid #fff;
            box-shadow: 0 2px 4px rgba(0,0,0,0.2);
          }
        `}
      </style>
      <div 
        data-shape-toolbar="true"
        style={{
          position: 'fixed',
          left: x + 150,
          top: y - 140,
          background: '#ffffff',
          borderRadius: 12,
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
          border: '1px solid #e5e7eb',
          width: 380,
          zIndex: 1000,
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
      {/* Заголовок */}
      <div style={{
        padding: '16px 20px 12px',
        borderBottom: '1px solid #f3f4f6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{
          fontSize: 14,
          fontWeight: 600,
          color: '#111827'
        }}>
          Shape Settings
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'none',
            border: 'none',
            fontSize: 18,
            color: '#6b7280',
            cursor: 'pointer',
            padding: 0,
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 4
          }}
          onMouseEnter={(e) => e.target.style.background = '#f3f4f6'}
          onMouseLeave={(e) => e.target.style.background = 'none'}
        >
          ×
        </button>
      </div>

      {/* Табы */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid #f3f4f6'
      }}>
        {[
          ...((currentShape.type !== 'line' && currentShape.type !== 'arrow') ? [{ key: 'fill', label: 'Fill', icon: <IconFill /> }] : []),
          { key: 'stroke', label: 'Stroke', icon: <IconStroke /> },
          ...(currentShape.type === 'line' ? [{ key: 'ends', label: 'Ends', icon: <IconThickness /> }] : []),
          { key: 'style', label: 'Style', icon: <IconStyle /> },
          { key: 'arrange', label: 'Arrange', icon: <IconBringForward /> }
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            style={{
              flex: 1,
              padding: '12px 16px',
              background: activeTab === tab.key ? '#f3f4f6' : 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 13,
              color: activeTab === tab.key ? '#111827' : '#6b7280',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (activeTab !== tab.key) {
                e.target.style.background = '#f9fafb';
              }
            }}
            onMouseLeave={(e) => {
              if (activeTab !== tab.key) {
                e.target.style.background = 'none';
              }
            }}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Контент табов */}
      <div style={{ padding: '16px 20px' }}>
        {activeTab === 'fill' && (
          <div>
            <div style={{ marginBottom: 12 }}>
              <div style={{
                fontSize: 12,
                fontWeight: 500,
                color: '#374151',
                marginBottom: 8
              }}>
                Fill Color
              </div>
              
              {/* Кнопка "Без заливки" */}
              <button
                onClick={() => handleFillChange('none')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: 6,
                  background: currentShape.fill === 'none' ? '#f3f4f6' : '#fff',
                  cursor: 'pointer',
                  fontSize: 13,
                  color: '#374151',
                  transition: 'all 0.2s',
                  width: '100%',
                  marginBottom: 12
                }}
                onMouseEnter={(e) => {
                  if (currentShape.fill !== 'none') {
                    e.target.style.background = '#f9fafb';
                    e.target.style.borderColor = '#d1d5db';
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentShape.fill !== 'none') {
                    e.target.style.background = '#fff';
                    e.target.style.borderColor = '#e5e7eb';
                  }
                }}
              >
                <div style={{
                  width: 16,
                  height: 16,
                  border: '2px solid #d1d5db',
                  borderRadius: 3,
                  background: 'repeating-linear-gradient(45deg, transparent, transparent 2px, #d1d5db 2px, #d1d5db 4px)'
                }} />
                No Fill
              </button>

              {/* Сетка цветов */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(10, 1fr)',
                gap: 4
              }}>
                {SHAPE_COLORS.map((color, index) => (
                  <button
                    key={index}
                    onClick={() => handleFillChange(color)}
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 4,
                      border: currentShape.fill === color ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      background: color,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.1)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.boxShadow = 'none';
                    }}
                    title={color}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'stroke' && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: 12,
                fontWeight: 500,
                color: '#374151',
                marginBottom: 8
              }}>
                Stroke Color
              </div>
              
              {/* Кнопка "No Stroke" - только для фигур, которые не являются линиями */}
              {currentShape.type !== 'line' && (
                <button
                  onClick={() => handleStrokeStyleChange('no stroke')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    background: currentShape.strokeStyle === 'no stroke' ? '#f3f4f6' : '#fff',
                    cursor: 'pointer',
                    fontSize: 13,
                    color: '#374151',
                    transition: 'all 0.2s',
                    width: '100%',
                    marginBottom: 12
                  }}
                  onMouseEnter={(e) => {
                    if (currentShape.strokeStyle !== 'no stroke') {
                      e.target.style.background = '#f9fafb';
                      e.target.style.borderColor = '#d1d5db';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (currentShape.strokeStyle !== 'no stroke') {
                      e.target.style.background = '#fff';
                      e.target.style.borderColor = '#e5e7eb';
                    }
                  }}
                >
                  <div style={{
                    width: 16,
                    height: 16,
                    border: '2px solid #d1d5db',
                    borderRadius: 3,
                    background: 'repeating-linear-gradient(45deg, transparent, transparent 2px, #d1d5db 2px, #d1d5db 4px)'
                  }} />
                  No Stroke
                </button>
              )}
              
              {/* Сетка цветов обводки */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(8, 1fr)',
                gap: 4
              }}>
                {STROKE_COLORS.map((color, index) => (
                  <button
                    key={index}
                    onClick={() => handleStrokeChange(color)}
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 4,
                      border: currentShape.color === color ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      background: color,
                      cursor: 'pointer',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = 'scale(1.1)';
                      e.target.style.boxShadow = '0 2px 8px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = 'scale(1)';
                      e.target.style.boxShadow = 'none';
                    }}
                    title={color}
                  />
                ))}
              </div>
            </div>

            <div>
              <div style={{
                fontSize: 12,
                fontWeight: 500,
                color: '#374151',
                marginBottom: 8
              }}>
                Stroke Width
              </div>
              
              <div style={{
                position: 'relative',
                padding: '8px 0'
              }}>
                <input
                  type="range"
                  min="1"
                  max="20"
                  step="1"
                  value={currentShape.strokeWidth || 1}
                  onChange={(e) => handleStrokeWidthChange(parseInt(e.target.value))}
                  style={{
                    width: '100%',
                    height: 6,
                    borderRadius: 3,
                    background: 'linear-gradient(to right, #e5e7eb 0%, #3b82f6 100%)',
                    outline: 'none',
                    cursor: 'pointer',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none'
                  }}
                  onInput={(e) => {
                    const percentage = ((e.target.value - 1) / 19) * 100;
                    e.target.style.background = `linear-gradient(to right, #e5e7eb 0%, #3b82f6 ${percentage}%)`;
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                />
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 10,
                  color: '#6b7280',
                  marginTop: 4
                }}>
                  <span>1px</span>
                  <span>20px</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'ends' && (
          <div>
            {/* Левый конец */}
            <div style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: 12,
                fontWeight: 500,
                color: '#374151',
                marginBottom: 8
              }}>
                Start End
              </div>
              
              <div style={{
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap'
              }}>
                {LINE_END_STYLES.map((style) => (
                  <button
                    key={`start-${style.value}`}
                    onClick={() => handleLineEndStyleChange(style.value, 'start')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '8px 12px',
                      border: currentShape.startEndStyle === style.value ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      borderRadius: 6,
                      background: currentShape.startEndStyle === style.value ? '#eff6ff' : '#fff',
                      cursor: 'pointer',
                      fontSize: 13,
                      color: '#374151',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (currentShape.startEndStyle !== style.value) {
                        e.target.style.background = '#f9fafb';
                        e.target.style.borderColor = '#d1d5db';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentShape.startEndStyle !== style.value) {
                        e.target.style.background = '#fff';
                        e.target.style.borderColor = '#e5e7eb';
                      }
                    }}
                  >
                    <span style={{ fontSize: 16 }}>{style.icon}</span>
                    {style.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Правый конец */}
            <div style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: 12,
                fontWeight: 500,
                color: '#374151',
                marginBottom: 8
              }}>
                End End
              </div>
              
              <div style={{
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap'
              }}>
                {LINE_END_STYLES.map((style) => (
                  <button
                    key={`end-${style.value}`}
                    onClick={() => handleLineEndStyleChange(style.value, 'end')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '8px 12px',
                      border: currentShape.endEndStyle === style.value ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      borderRadius: 6,
                      background: currentShape.endEndStyle === style.value ? '#eff6ff' : '#fff',
                      cursor: 'pointer',
                      fontSize: 13,
                      color: '#374151',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (currentShape.endEndStyle !== style.value) {
                        e.target.style.background = '#f9fafb';
                        e.target.style.borderColor = '#d1d5db';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentShape.endEndStyle !== style.value) {
                        e.target.style.background = '#fff';
                        e.target.style.borderColor = '#e5e7eb';
                      }
                    }}
                  >
                    <span style={{ fontSize: 16 }}>{style.icon}</span>
                    {style.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'style' && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: 12,
                fontWeight: 500,
                color: '#374151',
                marginBottom: 8
              }}>
                Stroke Style
              </div>
              
              <div style={{
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap'
              }}>
                {STROKE_STYLES.map((style) => (
                  <button
                    key={style.value}
                    onClick={() => handleStrokeStyleChange(style.value)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      padding: '8px 12px',
                      border: currentShape.strokeStyle === style.value ? '2px solid #3b82f6' : '1px solid #e5e7eb',
                      borderRadius: 6,
                      background: currentShape.strokeStyle === style.value ? '#eff6ff' : '#fff',
                      cursor: 'pointer',
                      fontSize: 13,
                      color: '#374151',
                      transition: 'all 0.2s'
                    }}
                    onMouseEnter={(e) => {
                      if (currentShape.strokeStyle !== style.value) {
                        e.target.style.background = '#f9fafb';
                        e.target.style.borderColor = '#d1d5db';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (currentShape.strokeStyle !== style.value) {
                        e.target.style.background = '#fff';
                        e.target.style.borderColor = '#e5e7eb';
                      }
                    }}
                  >
                    <span style={{ fontSize: 16 }}>{style.icon}</span>
                    {style.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: 12,
                fontWeight: 500,
                color: '#374151',
                marginBottom: 8,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <span>Opacity</span>
                <span style={{ fontSize: 11, color: '#6b7280' }}>
                  {Math.round((currentShape.opacity !== undefined ? currentShape.opacity : 1) * 100)}%
                </span>
              </div>
              
              <div style={{
                position: 'relative',
                padding: '8px 0'
              }}>
                <input
                  type="range"
                  min="0.01"
                  max="1"
                  step="0.01"
                  value={currentShape.opacity !== undefined ? currentShape.opacity : 1}
                  onChange={(e) => handleOpacityChange(parseFloat(e.target.value))}
                  style={{
                    width: '100%',
                    height: 6,
                    borderRadius: 3,
                    background: 'linear-gradient(to right, #e5e7eb 0%, #3b82f6 100%)',
                    outline: 'none',
                    cursor: 'pointer',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    appearance: 'none'
                  }}
                  onInput={(e) => {
                    const percentage = ((e.target.value - 0.01) / 0.99) * 100;
                    e.target.style.background = `linear-gradient(to right, #e5e7eb 0%, #3b82f6 ${percentage}%)`;
                  }}
                />
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: 10,
                  color: '#6b7280',
                  marginTop: 4
                }}>
                  <span>1%</span>
                  <span>100%</span>
                </div>
              </div>
            </div>

            {currentShape.type !== 'triangle' && currentShape.type !== 'message' && currentShape.type !== 'circle' && currentShape.type !== 'line' && currentShape.type !== 'arrow' && (
              <div>
                <div style={{
                  fontSize: 12,
                  fontWeight: 500,
                  color: '#374151',
                  marginBottom: 8,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span>Corner Radius</span>
                  <span style={{ fontSize: 11, color: '#6b7280' }}>
                    {currentShape.borderRadius !== undefined ? currentShape.borderRadius : 12}px
                  </span>
                </div>
                
                <div style={{
                  position: 'relative',
                  padding: '8px 0'
                }}>
                  <input
                    type="range"
                    min="0"
                    max="20"
                    step="1"
                    value={currentShape.borderRadius !== undefined ? currentShape.borderRadius : 12}
                    onChange={(e) => handleBorderRadiusChange(parseInt(e.target.value))}
                    style={{
                      width: '100%',
                      height: 6,
                      borderRadius: 3,
                      background: 'linear-gradient(to right, #e5e7eb 0%, #3b82f6 100%)',
                      outline: 'none',
                      cursor: 'pointer',
                      WebkitAppearance: 'none',
                      appearance: 'none'
                    }}
                    onInput={(e) => {
                      const percentage = (e.target.value / 20) * 100;
                      e.target.style.background = `linear-gradient(to right, #e5e7eb 0%, #3b82f6 ${percentage}%)`;
                    }}
                  />
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 10,
                    color: '#6b7280',
                    marginTop: 4
                  }}>
                    <span>Square</span>
                    <span>Round</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'arrange' && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <div style={{
                fontSize: 12,
                fontWeight: 500,
                color: '#374151',
                marginBottom: 8
              }}>
                Actions
              </div>
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8
              }}>
                <button
                  onClick={handleDuplicate}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    background: '#fff',
                    cursor: 'pointer',
                    fontSize: 13,
                    color: '#374151',
                    transition: 'all 0.2s',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#f9fafb';
                    e.target.style.borderColor = '#d1d5db';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#fff';
                    e.target.style.borderColor = '#e5e7eb';
                  }}
                >
                  <IconDuplicate />
                  Duplicate
                </button>

                <button
                  onClick={handleBringForward}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    background: '#fff',
                    cursor: 'pointer',
                    fontSize: 13,
                    color: '#374151',
                    transition: 'all 0.2s',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#f9fafb';
                    e.target.style.borderColor = '#d1d5db';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#fff';
                    e.target.style.borderColor = '#e5e7eb';
                  }}
                >
                  <IconBringForward />
                  Bring Forward
                </button>

                <button
                  onClick={handleSendBackward}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    background: '#fff',
                    cursor: 'pointer',
                    fontSize: 13,
                    color: '#374151',
                    transition: 'all 0.2s',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#f9fafb';
                    e.target.style.borderColor = '#d1d5db';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#fff';
                    e.target.style.borderColor = '#e5e7eb';
                  }}
                >
                  <IconSendBackward />
                  Send Backward
                </button>

                <button
                  onClick={handleDelete}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 12px',
                    border: '1px solid #fee2e2',
                    borderRadius: 6,
                    background: '#fef2f2',
                    cursor: 'pointer',
                    fontSize: 13,
                    color: '#dc2626',
                    transition: 'all 0.2s',
                    width: '100%'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.background = '#fee2e2';
                    e.target.style.borderColor = '#fca5a5';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.background = '#fef2f2';
                    e.target.style.borderColor = '#fee2e2';
                  }}
                >
                  <IconDelete />
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>,
    document.body
  );
};

export default ShapeToolbar; 